import { ProductProps } from '@/types/Product';

export interface EcommerceItem {
  item_id: string;
  item_name: string;
  item_brand?: string;
  item_category?: string;
  price: number;
  quantity?: number;
}

/**
 * Envia um evento para o dataLayer do GTM — segue a estrutura de
 * e-commerce recomendada pela Google para o GA4. Limpa o "ecommerce"
 * anterior antes de cada evento (recomendação da Google, evita misturar
 * dados de eventos diferentes).
 */
/**
 * Type guard para filtrar itens de carrinho/encomenda sem produto (ex.:
 * produto entretanto apagado/desativado) antes de os passar a
 * `toEcommerceItem` — estreita o tipo para o TypeScript aceitar o `.product`
 * como não-nulo depois do `.filter`.
 */
export function hasProduct<T extends { product: unknown }>(
  item: T,
): item is T & { product: NonNullable<T['product']> } {
  return !!item.product;
}

export function pushDataLayerEvent(
  event: string,
  data: Record<string, unknown> = {},
): void {
  if (typeof window === 'undefined') return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({ event, ...data });
}

/**
 * Constrói o `item` de e-commerce (item_id/name/brand/category/price) a
 * partir de um produto — escolhe o preço/referência certos consoante o
 * tipo (simples, variação, cor+tamanho, lentes de contacto), o mesmo
 * critério já usado na página do produto para o JSON-LD.
 */
export function toEcommerceItem(
  product: ProductProps,
  quantity?: number,
): EcommerceItem {
  let price = 0;
  // A mesma referência/SKU usada no backoffice e no feed do Google
  // Merchant Center (ver src/app/feed/google-merchant.xml/route.ts) — não
  // o id interno do produto.
  let reference = product.id;

  if (product.simple) {
    price = product.simple.price_tax;
    reference = product.simple.reference;
  } else if (product.variations && product.variations.length > 0) {
    price = product.variations[0].price_tax;
    reference = product.variations[0].reference;
  } else if (product.colors && product.colors.length > 0) {
    const firstSize = product.colors[0].sizes?.[0];
    price = firstSize?.price_tax ?? 0;
    reference = firstSize?.reference ?? product.id;
  } else if (product.contact_lenses) {
    price = product.contact_lenses.price_tax;
    reference = product.contact_lenses.reference;
  }

  return {
    item_id: reference,
    item_name: product.name,
    item_brand: product.brand?.name,
    item_category: product.categorie_main?.name,
    price,
    ...(quantity != null ? { quantity } : {}),
  };
}
